import { Navigate, Route, Routes, useParams } from 'react-router'
import { ModeProvider, type Mode } from '@/app/mode'
import { useAuth } from '@/auth/AuthProvider'
import { useClient, usePortalConfig, enabledSections } from '@/data/client'
import { useDoc } from '@/data/hooks'
import { PracticeProvider } from '@/data/practice'
import { EmptyState } from '@/ui'
import { ClientHome } from './ClientHome'
import { GalleriesPage } from './GalleriesPage'
import { PeoplePage } from './PeoplePage'
import { ReviewPage } from './ReviewPage'
import { SectionPage } from './SectionPage'
import { SubmittedPage } from './SubmittedPage'
import { TeamPage } from './TeamPage'
import { WelcomePage } from './WelcomePage'
import { WorkspaceLayout } from './WorkspaceLayout'

/** The client portal for one client. Staff reuse it in prep/preview mode from the console. */
export function ClientRoutes({ mode = 'client' }: { mode?: Mode }) {
  const { clientId = '' } = useParams()
  const { role } = useAuth()
  const client = useClient(clientId)
  const { config } = usePortalConfig(clientId)
  const base = mode === 'client' ? `/c/${clientId}` : `/staff/clients/${clientId}/${mode}`

  if (client.error && role !== 'staff') {
    return (
      <main id="main" className="grid min-h-dvh place-items-center px-6">
        <EmptyState title="You don’t have access to this portal." className="max-w-md">Ask your Sonder contact to add you.</EmptyState>
      </main>
    )
  }
  if (!client.data || !config) return null
  const first = enabledSections(config)[0]?.id ?? 'brands'

  return (
    <ModeProvider mode={mode}>
      {/* Previewing as the client is practice: it behaves like the client's portal but saves nothing. */}
      <PracticeProvider active={mode === 'preview'}>
        <Routes>
          <Route index element={<HomeGate mode={mode} clientId={clientId} client={client.data} config={config} base={base} />} />
          <Route path="welcome" element={<WelcomePage clientId={clientId} client={client.data} config={config} base={base} />} />
          <Route path="team" element={<TeamPage clientId={clientId} client={client.data} config={config} base={base} />} />
          <Route path="onboarding" element={<WorkspaceLayout clientId={clientId} client={client.data} config={config} base={base} />}>
            <Route index element={<Navigate to={first} replace />} />
            <Route path="review" element={<ReviewPage clientId={clientId} client={client.data} config={config} base={base} />} />
            <Route path=":sectionId" element={<SectionPage clientId={clientId} config={config} base={base} />} />
          </Route>
          <Route path="submitted" element={<SubmittedPage clientId={clientId} client={client.data} base={base} />} />
          <Route path="people" element={<PeoplePage clientId={clientId} client={client.data} base={base} />} />
          <Route path="galleries" element={<GalleriesPage clientId={clientId} client={client.data} base={base} />} />
          <Route path="*" element={<Navigate to={base} replace />} />
        </Routes>
      </PracticeProvider>
    </ModeProvider>
  )
}

/**
 * A client's first visit starts with the introduction (welcome, then their team); once they have been through it
 * to the onboarding, their address opens the home page instead. Staff always get the home page; the introduction
 * is a link from it. An admin signed in as someone chooses: "First visit" starts at the introduction, otherwise home.
 */
function HomeGate({ mode, ...props }: PageProps & { mode: Mode }) {
  const { user, impersonation } = useAuth()
  const me = useDoc<{ introSeen?: Record<string, number> }>(mode === 'client' && user && !impersonation ? `users/${user.uid}` : null)
  if (mode !== 'client') return <ClientHome {...props} />
  if (impersonation) return impersonation.firstVisit ? <Navigate to={`${props.base}/welcome`} replace /> : <ClientHome {...props} />
  if (me.loading) return null
  return me.data?.introSeen?.[props.clientId] ? <ClientHome {...props} /> : <Navigate to={`${props.base}/welcome`} replace />
}

export type PageProps = { clientId: string; client: import('@sonder/shared').ClientDoc; config: import('@sonder/shared').PortalConfig; base: string }
